import React,{useState,useEffect,useCallback}from'react';
import Layout from'../components/layout/Layout';
import Modal from'../components/common/Modal';
import Badge from'../components/common/Badge';
import StatCard from'../components/common/StatCard';
import api from'../services/api';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import DoctorSearchInput from '../components/common/DoctorSearchInput';
import {useTranslation} from 'react-i18next';
import FileShaClaimButton from '../components/common/FileShaClaimButton';

export default function TelemedicinePage(){
  const {t}=useTranslation();
  const [sessions,setSessions]=useState<any[]>([]);
  const [consults,setConsults]=useState<any[]>([]);
  const [tab,setTab]=useState('upcoming');
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState('');
  const [showNew,setShowNew]=useState(false);
  const [form,setForm]=useState({patientId:'',doctorId:'',scheduledAt:'',sessionType:'VIDEO',chiefComplaint:'',platform:'ZOOM',durationMins:'30',
    consultationNumber:'',
    endedAt:'',
    roomUrl:'',
    hostToken:'',
    participantToken:'',
    patientAccountId:'',
    doctorJoinUrl:'',
    actualDurationMinutes:'',
    prescriptionId:'',
    roomStatus:''
  });
  const [stats,setStats]=useState({upcoming:0,today:0,completed:0,cancelled:0});
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const [s,c]=await Promise.all([
        api.getTelemedicineSessions?.().catch(()=>({data:[]})),
        api.getTeleconsultations?.().catch(()=>({data:[]})),
      ]);
      const ss=Array.isArray(s?.data)?s.data:s?.data?.data||[];
      const cc=Array.isArray(c?.data)?c.data:c?.data?.data||[];
      setSessions(ss);setConsults(cc);
      const today=new Date().toISOString().split('T')[0];
      setStats({upcoming:ss.filter((x:any)=>x.status==='SCHEDULED').length,today:ss.filter((x:any)=>x.scheduled_at?.startsWith(today)).length,
        completed:ss.filter((x:any)=>x.status==='COMPLETED').length,cancelled:ss.filter((x:any)=>x.status==='CANCELLED').length});
    }catch{}
    setLoading(false);
  },[]);

  const createSession=async()=>{
    if(!form.patientId||!form.scheduledAt){setMsg('Patient and date/time required');return;}
    try{await api.createTelemedicineSession?.(form);notify('Session scheduled!');setShowNew(false);await load();}
    catch(e:any){notify('Error: '+(e.response?.data?.message||'failed'));}
  };

  const joinSession=async(session:any)=>{
    try{
      const r=await api.startTelemedicineSession?.(session.id);
      const url=r?.data?.join_url||r?.data?.joinUrl||session.meeting_url;
      if(url)window.open(url,'_blank');
      else notify('Session link not available');
    }catch(e:any){notify('Error joining session');}
  };

  useEffect(()=>{load();},[load]);

  const STATUS_COLOR:any={SCHEDULED:'blue',IN_PROGRESS:'green',COMPLETED:'gray',CANCELLED:'red',NO_SHOW:'orange'};

  return(
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div><h1 className="text-2xl font-bold text-gray-900">Telemedicine</h1><p className="text-gray-500">Video consultations and remote patient care</p></div>
          <button onClick={()=>setShowNew(true)} className="bg-[#02357A] text-white px-4 py-2 rounded-lg hover:bg-[#015CA6] font-medium">+ Schedule Session</button>
        </div>
        {msg&&<div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">{msg}</div>}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard label="Upcoming Sessions" value={stats.upcoming} color="blue"/>
          <StatCard label="Today's Sessions" value={stats.today} color="green"/>
          <StatCard label="Completed" value={stats.completed} color="gray"/>
          <StatCard label="Cancelled" value={stats.cancelled} color="red"/>
        </div>

        <div className="flex gap-2 mb-6">
          {[['upcoming','Upcoming'],['today','Today'],['completed','Completed'],['all','All Sessions']].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} className={`px-5 py-2 rounded-full text-sm font-medium ${tab===k?'bg-[#02357A] text-white':'bg-white border text-gray-600 hover:border-blue-300'}`}>{l}</button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Sessions List */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <h3 className="font-semibold">Video Sessions</h3>
              {loading&&<span className="text-xs text-gray-500 animate-pulse">{t('common.loading','Loading…')}</span>}
            </div>
            <div className="divide-y overflow-y-auto" style={{maxHeight:'72vh'}}>
              {sessions.length===0?<div className="p-12 text-center text-gray-400"><div className="text-4xl mb-3">📹</div><p>No sessions found</p><button onClick={()=>setShowNew(true)} className="mt-3 text-[#02357A] text-sm underline">Schedule one</button></div>:
                sessions.filter((s:any)=>tab==='all'||tab==='upcoming'&&s.status==='SCHEDULED'||tab==='today'&&s.scheduled_at?.startsWith(new Date().toISOString().split('T')[0])||tab==='completed'&&s.status==='COMPLETED')
                .map((s:any)=>(
                  <div key={s.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{s.patient_name||s.patientName||'Patient'}</p>
                        <p className="text-sm text-gray-500">Dr. {s.doctor_name||s.doctorName||'—'}</p>
                        <p className="text-xs text-gray-400 mt-1">{s.scheduled_at?.replace('T',' ').substring(0,16)||'—'} • {s.duration_mins||30} min • {s.session_type||'VIDEO'}</p>
                        {s.chief_complaint&&<p className="text-xs text-gray-600 mt-1 italic">"{s.chief_complaint}"</p>}
                      </div>
                      <div className="text-right">
                        <Badge text={s.status||'SCHEDULED'} color={STATUS_COLOR[s.status]||'blue'}/>
                        {s.status==='SCHEDULED'&&(
                          <button onClick={()=>joinSession(s)} className="mt-2 block text-xs bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700">🎥 Join</button>
                        )}
                        {(s.patientId||s.patient_id)&&<div className="mt-2"><FileShaClaimButton patientId={s.patientId||s.patient_id} patientName={s.patient_name||s.patientName} encounterId={s.id} serviceName="Telemedicine consultation" from="telemedicine"/></div>}
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Today's Schedule */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <h3 className="font-semibold text-gray-800 mb-4">Today's Schedule</h3>
              {sessions.filter((s:any)=>s.scheduled_at?.startsWith(new Date().toISOString().split('T')[0])).length===0
                ?<p className="text-gray-400 text-center py-6">No sessions today</p>
                :sessions.filter((s:any)=>s.scheduled_at?.startsWith(new Date().toISOString().split('T')[0])).map((s:any)=>(
                  <div key={s.id} className={`flex items-center gap-3 p-3 rounded-lg mb-2 ${s.status==='IN_PROGRESS'?'bg-green-50 border border-green-200':s.status==='COMPLETED'?'bg-gray-50':'bg-blue-50 border border-blue-200'}`}>
                    <div className="text-2xl">{s.status==='IN_PROGRESS'?'🔴':s.status==='COMPLETED'?'✅':'📅'}</div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{s.scheduled_at?.split('T')[1]?.substring(0,5)||'—'} — {s.patient_name||'Patient'}</p>
                      <p className="text-xs text-gray-500">Dr. {s.doctor_name||'—'} • {s.session_type||'VIDEO'}</p>
                    </div>
                    {s.status==='SCHEDULED'&&<button onClick={()=>joinSession(s)} className="text-xs bg-[#02357A] text-white px-2 py-1 rounded-lg">{t('common.start','Start')}</button>}
                  </div>
                ))
              }
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-[#039089] rounded-xl p-5 text-white">
              <h3 className="font-bold text-lg mb-2">Telemedicine Features</h3>
              <ul className="space-y-2 text-sm text-blue-100">
                {['HD Video Consultations via Zoom/Teams','Screen sharing for report review','Digital prescription generation','Automatic session recording','Patient consent documentation','SMS/WhatsApp reminders sent automatically','eMAR integration for medication orders'].map(f=><li key={f} className="flex items-center gap-2"><span>✓</span>{f}</li>)}
              </ul>
            </div>
          </div>
        </div>

        <Modal isOpen={showNew} onClose={()=>setShowNew(false)} title="Schedule Telemedicine Session">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div style={{isolation:"isolate",position:"relative"}}><PatientSearchInput label="Patient *" selected={null} onSelect={(p:any)=>setForm(f=>({...f,patientId:p.id}))} onClear={()=>setForm(f=>({...f,patientId:""}))} placeholder="Search patient..."/></div>
              <div style={{isolation:"isolate",position:"relative"}}><DoctorSearchInput label="Doctor" onSelect={(d:any)=>setForm(f=>({...f,doctorId:d.id}))} onClear={()=>setForm(f=>({...f,doctorId:""}))} placeholder="Search doctor by name..."/></div>
              <div><label className="block text-sm font-medium mb-1">Date & Time *</label><input type="datetime-local" value={form.scheduledAt} onChange={e=>setForm(f=>({...f,scheduledAt:e.target.value}))} className="w-full border rounded-lg p-2"/></div>
              <div><label className="block text-sm font-medium mb-1">Duration (minutes)</label><select value={form.durationMins} onChange={e=>setForm(f=>({...f,durationMins:e.target.value}))} className="w-full border rounded-lg p-2">{['15','20','30','45','60'].map(d=><option key={d}>{d}</option>)}</select></div>
              <div><label className="block text-sm font-medium mb-1">Session Type</label><select value={form.sessionType} onChange={e=>setForm(f=>({...f,sessionType:e.target.value}))} className="w-full border rounded-lg p-2">{['VIDEO','AUDIO','CHAT'].map(t=><option key={t}>{t}</option>)}</select></div>
              <div><label className="block text-sm font-medium mb-1">Platform</label><select value={form.platform} onChange={e=>setForm(f=>({...f,platform:e.target.value}))} className="w-full border rounded-lg p-2">{['ZOOM','TEAMS','GOOGLE_MEET','INTERNAL'].map(p=><option key={p}>{p}</option>)}</select></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Chief Complaint</label><input value={form.chiefComplaint} onChange={e=>setForm(f=>({...f,chiefComplaint:e.target.value}))} className="w-full border rounded-lg p-2" placeholder="Reason for teleconsult"/></div>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>setShowNew(false)} className="px-4 py-2 border rounded-lg">{t('common.cancel','Cancel')}</button>
              <button onClick={createSession} className="px-4 py-2 bg-[#02357A] text-white rounded-lg">Schedule Session</button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
}
